/**
 * Where the login screen sends the user afterwards. `PrivateRoute` stores the guarded location it
 * bounced from in router state (`{ from: location }`); this turns that state into an internal path
 * or falls back to `/`. Internal only: a `//host` or an absolute URL in the state must never become
 * a redirect target.
 */
export interface LocationLike {
  pathname: string;
  search?: string;
  hash?: string;
}

const isLocationLike = (value: unknown): value is LocationLike =>
  typeof value === 'object' &&
  value !== null &&
  'pathname' in value &&
  typeof value.pathname === 'string';

export const returnToFromState = (state: unknown): string => {
  if (typeof state !== 'object' || state === null) {
    return '/';
  }

  const from = 'from' in state ? state.from : undefined;
  if (!isLocationLike(from)) {
    return '/';
  }

  const target = `${from.pathname}${from.search ?? ''}${from.hash ?? ''}`;
  return target.startsWith('/') && !target.startsWith('//') ? target : '/';
};
